import { inject, Injectable } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { Apollo } from 'apollo-angular';
import { map, MonoTypeOperatorFunction, Observable, retry, take, throwError, timer } from 'rxjs';

import {
	type MarkupImageUrlsResponse,
	MarkupsRepository,
	type MarkupsResponse,
	type PlanPageMarkupTargetContext,
} from '@efficiently/client-api-plans';
import { GraphQLClient } from '@efficiently/client-core-graphql';
import type {
	CreateMarkupInput,
	MarkupTargetInput,
	MarkupTargetType,
	UpdateMarkupInput,
} from '@efficiently/client-core-graphql-models';

import { CREATE_MARKUP } from '../queries/create-markup';
import { DELETE_MARKUP } from '../queries/delete-markup';
import { GET_MARKUP_IMAGE_URLS } from '../queries/get-markup-image-urls';
import { GET_MARKUPS } from '../queries/get-markups';
import { UPDATE_MARKUP } from '../queries/update-markup';

/**
 * `extensions.code` of a concurrent-write failure. The Markup contract §1 declares it **retryable by
 * design**: the server exhausted its own internal retries allocating a `sequenceNumber`, and the
 * caller is expected to back off rather than report a failure on first occurrence.
 *
 * `VALIDATION`, `NOT_FOUND` and `PORTAL_FORBIDDEN` are terminal and are deliberately absent here —
 * retrying any of them would turn a deterministic refusal into a slow one.
 */
const CONFLICT_ERROR_CODE = 'CONFLICT';

/**
 * Retries allowed **after** the first attempt, so three attempts in total. Bounded, and the **only**
 * `CONFLICT` policy the markup client has: the edit store saves through {@link MarkupsGqlRepository.update}
 * rather than retrying for itself, because two answers to one contract rule is one too many (KD-8).
 */
const CONFLICT_RETRY_COUNT = 2;

/** First backoff step; doubled per retry (200 ms, then 400 ms). */
const CONFLICT_RETRY_BASE_DELAY_MS = 200;

/**
 * Reads the code from `extensions.code`, never from the message string — the message is prose the
 * server is free to reword, the code is the contract.
 */
function isConflictError(error: unknown): boolean {
	if (!(error instanceof ApolloError)) {
		return false;
	}

	return error.graphQLErrors?.some(graphQLError => graphQLError.extensions?.code === CONFLICT_ERROR_CODE) ?? false;
}

/**
 * Retries a `CONFLICT` with exponential backoff and rethrows everything else untouched.
 *
 * Applied to the **write** paths only. `CONFLICT` is raised while allocating a `sequenceNumber`, so
 * a read cannot produce one; and `Apollo.mutate` is cold (`fromPromise` re-invokes on subscribe)
 * whereas `watchQuery().valueChanges` is backed by a live `ObservableQuery` that would replay the
 * same terminal error instead of re-issuing the request. A retry on `list` would therefore be
 * decoration, not behaviour.
 */
function retryOnConflict<T>(): MonoTypeOperatorFunction<T> {
	return retry({
		count: CONFLICT_RETRY_COUNT,
		delay: (error: unknown, retryIndex: number) => {
			if (!isConflictError(error)) {
				return throwError(() => error);
			}

			return timer(CONFLICT_RETRY_BASE_DELAY_MS * 2 ** (retryIndex - 1));
		},
	});
}

/**
 * Apollo implementation of {@link MarkupsRepository} against `core-api`.
 *
 * One markup root field per document (Markup contract §1) — hence five documents rather than one
 * with five fields; `SingleMarkupRootFieldGuard` rejects the latter with `VALIDATION`.
 *
 * Internal to this library: bound through `providePlansDataAccess()`, never injected directly.
 */
@Injectable()
export class MarkupsGqlRepository extends MarkupsRepository {
	private readonly apollo = inject(Apollo);

	public override list(
		projectId: string,
		targetType: MarkupTargetType,
		targetId: string,
		targetContext: PlanPageMarkupTargetContext,
	): Observable<MarkupsResponse> {
		return this.apollo
			.watchQuery({
				query: GET_MARKUPS,
				variables: {
					projectId,
					targetType,
					targetId,
					targetContext,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				map(({ data }) => data.markups),
			);
	}

	/**
	 * `apollo.query` rather than `watchQuery`: a one-shot read that stores nothing has nothing to watch,
	 * and it emits once and completes on its own, so no `take(1)` is needed here.
	 *
	 * `fetchPolicy: 'no-cache'` is load-bearing, not a default (TR-5). `MarkupImageUrl` carries no `id`,
	 * so Apollo would normalize the entries under the query's arguments and hand the same presigned URL
	 * back after its `expiresAt` — which the Markup contract forbids. Writing nothing to the cache also
	 * keeps a bearer URL out of a store the whole application can read.
	 *
	 * No `retryOnConflict`: `CONFLICT` is raised while allocating a `sequenceNumber`, which a read never
	 * does. Errors pass through untouched so the caller sees a `VALIDATION` (an over-long `imageRefs`)
	 * or a transport failure as it is, and decides for itself — the re-request policy lives with the
	 * caller that owns the page, not in the transport.
	 */
	public override imageUrls(
		projectId: string,
		target: MarkupTargetInput,
		imageRefs: readonly string[],
	): Observable<MarkupImageUrlsResponse> {
		return this.apollo
			.query({
				query: GET_MARKUP_IMAGE_URLS,
				variables: {
					projectId,
					target,
					// Copied rather than cast: the generated variables type is mutable, and the caller's
					// array is its own.
					imageRefs: [...imageRefs],
				},
				fetchPolicy: 'no-cache',
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(map(({ data }) => data.markupImageUrls));
	}

	public override create(input: CreateMarkupInput): Observable<MarkupsResponse[number]> {
		return this.apollo
			.mutate({
				mutation: CREATE_MARKUP,
				variables: {
					input,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				retryOnConflict(),
				take(1),
				map(({ data }) => data.createMarkup),
			);
	}

	public override update(id: string, input: UpdateMarkupInput): Observable<MarkupsResponse[number]> {
		return this.apollo
			.mutate({
				mutation: UPDATE_MARKUP,
				variables: {
					id,
					input,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				retryOnConflict(),
				take(1),
				map(({ data }) => data.updateMarkup),
			);
	}

	public override delete(id: string): Observable<string> {
		return this.apollo
			.mutate({
				mutation: DELETE_MARKUP,
				variables: {
					id,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				retryOnConflict(),
				take(1),
				map(({ data }) => data.deleteMarkup),
			);
	}
}
